/**
 * CodeMirror completion source backed by intelligence/complete.
 *
 * Async CompletionSource that delegates to the Engine's intelligence/complete
 * JSON-RPC method. Falls back to empty completions when the Engine or session
 * is unavailable.
 *
 * Handles both plain-text and snippet (${1:...} tab-stop) insertions.
 *
 * Filtering strategy:
 * - For directive context (`:`, `::`) we set `from` to right after the colons
 *   and strip `::` from labels so CM's fuzzy filter matches e.g. "ai-a" against
 *   "define ai-agent" without colon interference. The `apply` function prepends
 *   `::` back (or uses insertText which already contains it).
 * - For all other contexts we rely on CM's built-in filter directly.
 */

import {
    autocompletion,
    type Completion,
    type CompletionContext,
    type CompletionResult,
    snippetCompletion,
    startCompletion,
} from "@codemirror/autocomplete";
import { EditorView, ViewPlugin } from "@codemirror/view";
import type { IntelligenceCompletionKind } from "@magic-md/engine-client/protocol";
import { t as translate } from "@magic-md/i18n";
import { magicScanField } from "../codemirror-magic-syntax/scan-state";
import { createIntelligenceSessionResolver } from "./session";
import type { IntelligenceConfig } from "./types";

function tr(key: string, params?: Record<string, unknown>): string {
    return translate("editor", key, params);
}

/* ── Kind mapping ── */

const kindMap: Record<IntelligenceCompletionKind, string> = {
    plugin: "class",
    definition: "variable",
    field: "property",
    directive: "keyword",
    keyword: "keyword",
    snippet: "text",
    variable: "variable",
    file: "class",
    module: "namespace",
    namespace: "namespace",
};

const COMPLETION_TIMEOUT_MS = 800;
const completionTimeoutFallback: { timedOut: true } = { timedOut: true };

interface SlashCompletionContext {
    inline: boolean;
    slashStart: number;
    replaceFrom: number;
    filterFrom: number;
    partial: string;
}

interface AttributeValueCompletionContext {
    kind: "attribute-value";
    attribute: "from" | "pick" | "exclude";
    directive: "import" | "use" | "unknown";
    replaceFrom: number;
    value: string;
}

interface RefExpressionCompletionContext {
    kind: "ref-expression";
}

interface RenderTargetCompletionContext {
    kind: "render-target";
}

type StructuredCompletionContext =
    | AttributeValueCompletionContext
    | RefExpressionCompletionContext
    | RenderTargetCompletionContext;

export function getSlashCompletionContext(
    state: import("@codemirror/state").EditorState,
    line: { from: number; number: number },
    textBefore: string,
): SlashCompletionContext | null {
    if (isStructuredCompletionValueContext(textBefore)) return null;

    const slashLineStart = textBefore.match(/^(\s*)\/([\w-]*)$/);
    const slashInline = !slashLineStart
        ? textBefore.match(/[^/\w-]\/([\w-]*)$/)
        : null;
    if (!slashLineStart && !slashInline) return null;

    const inline = !!slashInline;
    if (!isSlashAllowedAt(state, line.number, inline)) return null;

    if (slashLineStart) {
        const existingIndent = slashLineStart[1] ?? "";
        const slashStart = line.from + existingIndent.length;
        return {
            inline: false,
            slashStart,
            replaceFrom: slashStart,
            filterFrom: slashStart + 1,
            partial: slashLineStart[2] ?? "",
        };
    }

    const slashStart =
        line.from + slashInline!.index! + slashInline![0].length - (slashInline![1]?.length ?? 0) - 1;
    return {
        inline: true,
        slashStart,
        replaceFrom: slashStart,
        filterFrom: slashStart + 1,
        partial: slashInline![1] ?? "",
    };
}

function isStructuredCompletionValueContext(textBefore: string): boolean {
    return (
        /\$ref\{[^}]*$/.test(textBefore) ||
        /(?:from|pick|exclude)="[^"]*$/.test(textBefore)
    );
}

export function getAttributeValueCompletionContext(
    textBefore: string,
    pos: number,
): AttributeValueCompletionContext | null {
    const attrValueMatch = textBefore.match(/\b(from|pick|exclude)="([^"]*)$/);
    if (!attrValueMatch) return null;
    const directive = /^\s*::import\{/.test(textBefore)
        ? "import"
        : /^\s*::use\[[^\]]+\]\{/.test(textBefore)
            ? "use"
            : "unknown";
    return {
        kind: "attribute-value",
        attribute: attrValueMatch[1] as "from" | "pick" | "exclude",
        directive,
        replaceFrom: pos - attrValueMatch[2].length,
        value: attrValueMatch[2],
    };
}

export function getStructuredCompletionContext(
    textBefore: string,
    pos: number,
): StructuredCompletionContext | null {
    if (/\$ref\{[^}]*$/.test(textBefore)) return { kind: "ref-expression" };
    const attrContext = getAttributeValueCompletionContext(textBefore, pos);
    if (attrContext) return attrContext;
    if (/::render\[[^\]]*$/.test(textBefore)) return { kind: "render-target" };
    return null;
}

function emptyLiveCompletion(pos: number): CompletionResult {
    return { from: pos, options: [], filter: false };
}

function completionTimeout(): Promise<typeof completionTimeoutFallback> {
    return new Promise((resolve) =>
        setTimeout(() => resolve(completionTimeoutFallback), COMPLETION_TIMEOUT_MS),
    );
}

function pushUniqueCompletion(
    options: Completion[],
    seen: Set<string>,
    completion: Completion,
) {
    if (seen.has(completion.label)) return;
    seen.add(completion.label);
    options.push(completion);
}

function renderTargetFallbackCompletion(
    label: string,
    type: string,
    detail: string,
    boost: number,
): Completion {
    return snippetCompletion(`\${1:${label}}`, {
        label,
        type,
        detail,
        boost,
    });
}

export function buildLocalStructuredCompletionFallback(
    state: import("@codemirror/state").EditorState,
    pos: number,
    textBefore: string,
): CompletionResult | null {
    const analysis = state.field(magicScanField, false);
    if (!analysis) return null;

    const renderMatch = textBefore.match(/::render\[([^\]]*)$/);
    if (renderMatch) {
        const partial = renderMatch[1] ?? "";
        const options: Completion[] = [];
        const seen = new Set<string>();

        for (const block of analysis.scan.defineBlocks) {
            pushUniqueCompletion(
                options,
                seen,
                renderTargetFallbackCompletion(
                    block.defId,
                    "variable",
                    block.pluginName
                        ? tr("completion.definitionWithPlugin", { pluginName: block.pluginName })
                        : tr("completion.definition"),
                    -options.length,
                ),
            );
        }
        for (const id of analysis.scan.localForIds) {
            pushUniqueCompletion(
                options,
                seen,
                renderTargetFallbackCompletion(
                    id,
                    "variable",
                    tr("completion.forLoop"),
                    -options.length,
                ),
            );
        }
        for (const id of analysis.scan.localContentIds) {
            pushUniqueCompletion(
                options,
                seen,
                renderTargetFallbackCompletion(
                    id,
                    "variable",
                    tr("completion.contentDefinition"),
                    -options.length,
                ),
            );
        }
        for (const id of analysis.scan.localUseIds) {
            pushUniqueCompletion(
                options,
                seen,
                renderTargetFallbackCompletion(
                    id,
                    "variable",
                    tr("completion.useInstance"),
                    -options.length,
                ),
            );
        }

        return {
            from: pos - partial.length,
            options,
            filter: true,
        };
    }

    const refBaseMatch = textBefore.match(/\$ref\{([^}.]*)$/);
    if (refBaseMatch) {
        const partial = refBaseMatch[1] ?? "";
        const options: Completion[] = [];
        const seen = new Set<string>();

        pushUniqueCompletion(options, seen, {
            label: "assets/",
            type: "namespace",
            detail: tr("completion.documentAssetsPackage"),
            apply: "assets/",
            boost: -options.length,
        });
        for (const block of analysis.scan.defineBlocks) {
            pushUniqueCompletion(options, seen, {
                label: block.defId,
                type: "variable",
                detail: block.pluginName ? `${block.pluginName} · .output` : ".output",
                apply: `${block.defId}.output.`,
                boost: -options.length,
            });
        }
        for (const id of analysis.scan.localForIds) {
            pushUniqueCompletion(options, seen, {
                label: id,
                type: "variable",
                detail: tr("completion.forOutput"),
                apply: `${id}.output.`,
                boost: -options.length,
            });
        }
        for (const id of analysis.scan.localContentIds) {
            pushUniqueCompletion(options, seen, {
                label: id,
                type: "variable",
                detail: tr("completion.contentBody"),
                apply: `${id}.body`,
                boost: -options.length,
            });
        }
        for (const id of analysis.scan.localUseIds) {
            pushUniqueCompletion(options, seen, {
                label: id,
                type: "variable",
                detail: tr("completion.useOutput"),
                apply: `${id}.output.`,
                boost: -options.length,
            });
        }

        return {
            from: pos - partial.length,
            options,
            filter: true,
        };
    }

    return null;
}

export function shouldRestartCompletionAfterApply(
    completionContext: StructuredCompletionContext | null,
    applyText: string,
): boolean {
    if (!completionContext) return false;
    if (completionContext.kind === "attribute-value") return applyText.endsWith("/");
    return applyText.endsWith("/") || applyText.endsWith(".");
}

export function getStructuredCompletionCursor(
    replaceFrom: number,
    applyText: string,
): number {
    return replaceFrom + applyText.length;
}

export function getStructuredCompletionApplyFrom(
    replaceFrom: number,
    charBeforeReplaceFrom: string,
    applyText: string,
    completionContext: StructuredCompletionContext | null,
): number {
    if (
        completionContext?.kind === "ref-expression" &&
        charBeforeReplaceFrom === "." &&
        applyText.startsWith("[")
    ) {
        return Math.max(0, replaceFrom - 1);
    }

    return replaceFrom;
}

/* ── CompletionSource factory ── */

function createIntelligenceCompletionSource(config: IntelligenceConfig) {
    const resolveSession = createIntelligenceSessionResolver(config);

    return async function intelligenceCompletions(
        context: CompletionContext,
    ): Promise<CompletionResult | null> {
        // Trigger on typing word chars, $, :, ., {, -, or on empty lines.
        // For path contexts (from="...", pick="...", ::render[ns/...]) we also
        // need to accept '/' — without this the source returns null on '/',
        // which closes the popup before the engine can provide updated items.
        const line = context.state.doc.lineAt(context.pos);
        const isEmptyLine = context.pos === line.from && line.text.trim() === "";
        const textBefore = context.state.doc.sliceString(line.from, context.pos);
        const structuredValueContext = isStructuredCompletionValueContext(textBefore);
        if (
            !context.explicit &&
            !isEmptyLine &&
            !structuredValueContext &&
            !context.matchBefore(/[\w$:.{/-]/)
        )
            return null;

        const localFallback = () =>
            buildLocalStructuredCompletionFallback(context.state, context.pos, textBefore);

        const localResult = localFallback();
        if (localResult && !config.getSessionId()) {
            void resolveSession().catch(() => null);
            return localResult;
        }

        const engine = config.getEngine();
        if (!engine?.intelligenceComplete) return localResult;

        const resolvedSession = await Promise.race([
            resolveSession(),
            completionTimeout(),
        ]);
        if (resolvedSession && "timedOut" in resolvedSession) {
            return localFallback() ?? emptyLiveCompletion(context.pos);
        }

        const session = resolvedSession;
        if (!session) return localFallback();

        const lineNumber = line.number; // 1-based
        const column = context.pos - line.from; // 0-based

        try {
            const complete = session.engine.intelligenceComplete;
            if (!complete) return null;

            // CM autocomplete is a UI-blocking flow: if the engine is
            // busy (e.g. processing a large sync) and takes > a few
            // hundred ms, the user experiences a frozen popup.  Race
            // against a soft timeout so the popup gracefully stays
            // empty rather than blocking the editor.  Empty result
            // (not null) keeps the popup alive so it can re-trigger
            // when the next keystroke arrives.
            const raced = await Promise.race([
                complete({
                    sessionId: session.sessionId,
                    line: lineNumber,
                    column,
                    locale: config.getLocale?.(),
                    lineText: context.state.doc.sliceString(line.from, line.to),
                }),
                completionTimeout(),
            ]);

            if (raced && "timedOut" in raced) {
                // Don't dismiss the popup permanently; return an empty
                // result with filter:false so CM keeps it open and the
                // next keystroke re-issues the request.
                return localFallback() ?? emptyLiveCompletion(context.pos);
            }
            const result = raced;

            if (!result?.items?.length) return localFallback();

            const slashContext = getSlashCompletionContext(context.state, line, textBefore);
            if (slashContext) {
                const options: Completion[] = result.items.map((item) => {
                    const displayLabel = item.label.replace(/^::/, "");
                    const applyText = item.insertText ?? item.label;
                    if (item.isSnippet && item.insertText) {
                        const snip = snippetCompletion(applyText, {
                            label: displayLabel,
                            type: kindMap[item.kind] ?? "text",
                            detail: item.detail,
                            info: item.documentation,
                            boost: item.sortOrder != null ? -item.sortOrder : undefined,
                        });
                        const origApply = snip.apply;
                        snip.apply = (view, completion, _from, to) => {
                            if (typeof origApply === "function") {
                                origApply(view, completion, slashContext.replaceFrom, to);
                            } else {
                                view.dispatch({
                                    changes: {
                                        from: slashContext.replaceFrom,
                                        to,
                                        insert: origApply ?? applyText,
                                    },
                                });
                            }
                        };
                        return snip;
                    }
                    return {
                        label: displayLabel,
                        type: kindMap[item.kind] ?? "text",
                        detail: item.detail,
                        info: item.documentation,
                        apply: (
                            view: import("@codemirror/view").EditorView,
                            _completion: Completion,
                            _from: number,
                            to: number,
                        ) => {
                            view.dispatch({
                                changes: {
                                    from: slashContext.replaceFrom,
                                    to,
                                    insert: applyText,
                                },
                            });
                        },
                        boost: item.sortOrder != null ? -item.sortOrder : undefined,
                    };
                });

                return {
                    // Filter on the partial after the leading `/`
                    from: slashContext.filterFrom,
                    options,
                    filter: true,
                };
            }

            // Detect directive context: :: at line start (including indented
            // lines inside ::for/::content bodies) OR inline.
            const solDirective = textBefore.match(/^(\s*)(:{1,2})([\w-]*)$/);
            const inlineDirective = !solDirective
                ? textBefore.match(/(::)([\w-]*)$/)
                : null;
            const directivePrefix = solDirective ?? inlineDirective;

            if (directivePrefix) {
                // Directive context —
                // Place `from` right after the colons so that CM's filter text
                // is just the keyword part (e.g. "ai-a" not "::ai-a").
                // Labels are stripped of `::` prefix for matching; the full
                // `::` text is restored via the `apply` / snippet insertText.
                //
                // For start-of-line directives, directiveStart = line.from.
                // For inline :: (e.g. "text ::render"), directiveStart = position of "::".
                const directiveStart = solDirective
                    ? line.from + solDirective[1].length
                    : line.from + directivePrefix.index!;
                const colonGroup = solDirective ? solDirective[2] : directivePrefix[1];
                const colonEnd = directiveStart + colonGroup.length;

                const options: Completion[] = result.items.map((item) => {
                    // Label without :: for CM fuzzy matching
                    const displayLabel = item.label.replace(/^::/, "");

                    if (item.isSnippet && item.insertText) {
                        const snip = snippetCompletion(item.insertText, {
                            label: displayLabel,
                            type: kindMap[item.kind] ?? "text",
                            detail: item.detail,
                            info: item.documentation,
                            boost: item.sortOrder != null ? -item.sortOrder : undefined,
                        });
                        // Override apply to replace from directive start (including colons)
                        const origApply = snip.apply;
                        snip.apply = (view, completion, _from, to) => {
                            if (typeof origApply === "function") {
                                origApply(view, completion, directiveStart, to);
                            } else {
                                view.dispatch({
                                    changes: {
                                        from: directiveStart,
                                        to,
                                        insert: origApply ?? item.insertText ?? item.label,
                                    },
                                });
                            }
                        };
                        return snip;
                    }

                    return {
                        label: displayLabel,
                        type: kindMap[item.kind] ?? "text",
                        detail: item.detail,
                        info: item.documentation,
                        // Apply replaces from directive start (including colons)
                        apply: (
                            view: import("@codemirror/view").EditorView,
                            _completion: Completion,
                            _from: number,
                            to: number,
                        ) => {
                            view.dispatch({
                                changes: {
                                    from: directiveStart,
                                    to,
                                    insert: item.insertText ?? item.label,
                                },
                            });
                        },
                        boost: item.sortOrder != null ? -item.sortOrder : undefined,
                    };
                });

                return {
                    from: colonEnd,
                    options,
                    filter: true,
                };
            }

            // Non-directive contexts — use CM's built-in filter
            const structuredContext = getStructuredCompletionContext(
                textBefore,
                context.pos,
            );
            const attrContext =
                structuredContext?.kind === "attribute-value" ? structuredContext : null;
            const from = attrContext?.replaceFrom ?? getCompletionFrom(textBefore, context.pos);
            const isRefBaseContext = /\$ref\{[^}.]*$/.test(textBefore);

            const options: Completion[] = result.items.map((item) => {
                if (item.isSnippet && item.insertText) {
                    return snippetCompletion(item.insertText, {
                        label: item.label,
                        type: kindMap[item.kind] ?? "text",
                        detail: item.detail,
                        info: item.documentation,
                        boost: item.sortOrder != null ? -item.sortOrder : undefined,
                    });
                }

                const applyText = getCompletionApplyText(item, isRefBaseContext);
                return {
                    label: item.label,
                    type: kindMap[item.kind] ?? "text",
                    detail: item.detail,
                    info: item.documentation,
                    apply: structuredContext
                        ? (
                            view: import("@codemirror/view").EditorView,
                            _completion: Completion,
                            completionFrom: number,
                            to: number,
                        ) => {
                            const initialFrom = attrContext?.replaceFrom ?? completionFrom;
                            const applyFrom = getStructuredCompletionApplyFrom(
                                initialFrom,
                                initialFrom > 0
                                    ? view.state.doc.sliceString(initialFrom - 1, initialFrom)
                                    : "",
                                applyText,
                                structuredContext,
                            );
                            view.dispatch({
                                changes: {
                                    from: applyFrom,
                                    to,
                                    insert: applyText,
                                },
                                selection: {
                                    anchor: getStructuredCompletionCursor(
                                        applyFrom,
                                        applyText,
                                    ),
                                },
                                userEvent: "input.complete",
                            });
                            if (shouldRestartCompletionAfterApply(structuredContext, applyText)) {
                                requestAnimationFrame(() => startCompletion(view));
                            }
                        }
                        : applyText,
                    boost: item.sortOrder != null ? -item.sortOrder : undefined,
                };
            });

            return {
                from,
                options,
                filter: attrContext?.directive === "import" && attrContext.attribute === "from"
                    ? false
                    : true,
            };
        } catch {
            // Don't return null — that would dismiss the popup and
            // prevent it from re-opening on the same trigger.  Return
            // an empty result with filter:false so CM keeps it alive
            // for the next keystroke.
            return localFallback() ?? emptyLiveCompletion(context.pos);
        }
    };
}

export function getCompletionApplyText(
    item: {
        label: string;
        kind: IntelligenceCompletionKind;
        insertText?: string;
    },
    isRefBaseContext: boolean,
): string {
    if (!isRefBaseContext) {
        return item.insertText ?? item.label;
    }

    // In $ref{<cursor>} base context, accepting a candidate should advance the
    // expression to the next canonical token whenever the engine provided one:
    // - definition      -> id.output.
    // - content define  -> id.body
    // - imported symbol -> ns/id.output. or ns/id.body
    // - namespace       -> ns/
    // Variables don't usually provide insertText, so they naturally fall back
    // to the label ($each, $each_index, input/foo).
    return item.insertText ?? item.label;
}

export function getCompletionFrom(textBefore: string, pos: number): number {
    // Inside $ref{...}: only filter on the currently typed fragment.
    // Examples:
    //   $ref{js            -> "js"
    //   $ref{foo.output.bo -> "bo"
    //   $ref{ns/id.output. -> ""
    const refMatch = textBefore.match(/\$ref\{([^}]*)$/);
    if (refMatch) {
        const refContent = refMatch[1];
        if (isAssetRefCompletionContent(refContent)) {
            return pos - refContent.length;
        }
        const filterText = refContent.split(".").pop() ?? refContent;
        return pos - filterText.length;
    }

    // Top-level $ trigger and partially typed $words should replace the full
    // currently typed token so accepting `$ref{}` does not produce `$$ref{}`.
    const dollarTokenMatch = textBefore.match(/\$[\w-]*$/);
    if (dollarTokenMatch) {
        return pos - dollarTokenMatch[0].length;
    }

    // Inside from="<partial>" or pick="<partial>": replace the entire value
    // typed so far so that accepting e.g. "./libs.md" doesn't produce
    // "././libs.md" when the user already typed "./".
    const attrValueMatch = textBefore.match(/(?:from|pick|exclude)="([^"]*)$/);
    if (attrValueMatch) {
        return pos - attrValueMatch[1].length;
    }

    // Generic token patterns:
    //   ns/id — for from="ns/id" in ::use blocks
    //   word-with-hyphens — regular tokens
    const tokenMatch = textBefore.match(/[\w-]+(?:\/[\w-]*)?$/);
    return tokenMatch ? pos - tokenMatch[0].length : pos;
}

function isAssetRefCompletionContent(refContent: string): boolean {
    return (
        refContent === "assets" ||
        refContent.startsWith("assets/") ||
        /^[\w-]+\/assets(?:\/.*)?$/.test(refContent)
    );
}

/* ── Slash trigger gating ── */

/**
 * Check whether `/` at the given (1-based) line should trigger the slash command picker.
 * Suppress inside opaque fences/config blocks, but allow content body lines because
 * Magic MD directives are valid continuation units there.
 */
export function isSlashAllowedAt(
    state: import("@codemirror/state").EditorState,
    lineNumber: number,
    _inline = false,
): boolean {
    const analysis = state.field(magicScanField, false);
    if (!analysis) return true; // be permissive if scan not yet ready
    const { scan } = analysis;
    if (scan.outputFenceLines.has(lineNumber)) return false;
    if (scan.mdFenceLines.has(lineNumber)) return false;
    // Inside an existing ::define / ::meta / ::use block (header + config fence + closer) — never allow.
    if (scan.defineBlockByLine[lineNumber]) return false;
    if (scan.metaBlockByLine[lineNumber]) return false;
    if (scan.useBlockByLine[lineNumber]) return false;
    const contentBlock = scan.contentBlockByLine[lineNumber];
    if (contentBlock) {
        return lineNumber > contentBlock.startLine && lineNumber < contentBlock.endLine;
    }
    return true;
}

/* ── Export ── */

export function intelligenceCompletion(config: IntelligenceConfig) {
    return [
        autocompletion({
            override: [createIntelligenceCompletionSource(config)],
            activateOnTyping: true,
            defaultKeymap: true,
            icons: true,
            optionClass: () => "cm-magic-completion-option",
            tooltipClass: () => "cm-magic-completion-tooltip",
        }),
        // Slash trigger: when the user types `/` (line-start OR mid-line),
        // proactively open the completion popup so the user gets a Notion-style
        // picker without needing Ctrl+Space. Inline slash → only `::render` variants.
        ViewPlugin.fromClass(class {
            update(update: import("@codemirror/view").ViewUpdate) {
                if (!update.docChanged) return;
                if (!update.transactions.some((tr) => tr.isUserEvent("input"))) return;

                const pos = update.state.selection.main.head;
                const line = update.state.doc.lineAt(pos);
                const textBefore = update.state.doc.sliceString(line.from, pos);
                // Match line-start `/` or inline `/` after a non-slash/word boundary
                const lineStart = /^\/$/.test(textBefore);
                const inline = !lineStart && /[^/\w-]\/$/.test(textBefore);
                if (!lineStart && !inline) return;

                if (!isSlashAllowedAt(update.state, line.number, inline)) return;
                startCompletion(update.view);
            }
        }),
        EditorView.updateListener.of((update) => {
            if (!update.docChanged || !update.state.selection.main.empty) return;

            const wasUserInput = update.transactions.some(
                (tr) => tr.isUserEvent("input") && !tr.isUserEvent("input.complete"),
            );
            if (!wasUserInput) return;

            let insertedText = "";
            for (const tr of update.transactions) {
                tr.changes.iterChanges((_fromA, _toA, _fromB, _toB, inserted) => {
                    insertedText += inserted.toString();
                });
            }

            if (!insertedText || insertedText.includes("\n")) return;

            const pos = update.state.selection.main.head;
            const line = update.state.doc.lineAt(pos);
            const textBefore = update.state.doc.sliceString(line.from, pos);

            if (!shouldAutoTriggerCompletion(textBefore, insertedText)) return;
            startCompletion(update.view);
        }),
    ];
}

export function shouldAutoTriggerCompletion(textBefore: string, insertedText: string): boolean {
    const lastChar = insertedText[insertedText.length - 1] ?? "";
    if (!lastChar) return false;

    const trailingRefKeywordMatch = textBefore.match(/\$r(?:e(?:f)?)?$/i);

    if (/^\s*:{1,2}[\w-]*$/.test(textBefore)) {
        return /[:\w-]/.test(lastChar);
    }

    // Inline :: directive (e.g., "some text ::rend")
    if (/::([\w-]*)$/.test(textBefore)) {
        return /[:\w-]/.test(lastChar);
    }

    if (trailingRefKeywordMatch) {
        return /[A-Za-z]/.test(lastChar);
    }

    if (/\$ref\{[^}]*$/.test(textBefore)) {
        return /[\w./${}-]/.test(lastChar);
    }

    if (/::render\[[^\]]*$/.test(textBefore)) {
        return /[\w/-]/.test(lastChar);
    }

    if (/::render\[[^\]]+\]\{[^}]*$/.test(textBefore)) {
        return lastChar !== "}";
    }

    // ::meta[<partial>] — trigger domain suggestions (module, output, config)
    if (/::meta\[[^\]]*$/.test(textBefore)) {
        return /[\w-]/.test(lastChar);
    }

    // Whole-config storage values should open immediately after the quote and
    // remain live while typing `external` / `inline`.
    if (/\bconfig="[^"]*$/.test(textBefore)) {
        return lastChar === '"' || /[\w-]/.test(lastChar);
    }

    // Engine intelligence owns the ::meta[config].mode candidates; CM only
    // keeps the popup live while the value is entered.
    if (/"mode"\s*:\s*"[^"]*$/.test(textBefore)) {
        return lastChar === '"' || /[\w-]/.test(lastChar);
    }

    if (/plugin="[^"]*$/.test(textBefore)) {
        return /[\w-]/.test(lastChar);
    }

    if (/from="\$[\w]*$/i.test(textBefore)) {
        return /[$A-Za-z]/.test(lastChar);
    }

    if (/from="[^"]*$/.test(textBefore)) {
        return lastChar !== '"' && lastChar !== "}";
    }

    // pick="<partial>" or exclude="<partial>" in ::import
    if (/(?:pick|exclude)="[^"]*$/.test(textBefore)) {
        return lastChar !== '"';
    }

    return false;
}
